import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Loader2, Leaf } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { useAdminAuth } from "./AdminAuthContext";

function formatError(detail) {
  if (!detail) return "Login failed. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : ""))
      .filter(Boolean)
      .join(" ");
  return "Login failed. Please try again.";
}

export default function AdminLogin() {
  const { user, checking, login } = useAdminAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  if (!checking && user) return <Navigate to="/admin/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error("Please enter email and password.");
      return;
    }
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      toast.success("Signed in");
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatError(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen flex items-center justify-center bg-secondary/40 px-5 py-12"
    >
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2.5 justify-center mb-8">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <Leaf className="h-5 w-5" />
          </span>
          <span className="font-display font-extrabold tracking-tight text-primary text-xl">
            GEES Admin
          </span>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="admin-login-form"
          className="rounded-2xl border border-border bg-white p-8 shadow-lg"
        >
          <div className="flex items-center gap-2 mb-1.5">
            <Lock className="h-4 w-4 text-primary" />
            <h1 className="font-display font-bold text-2xl text-foreground">
              Sign in
            </h1>
          </div>
          <p className="text-sm text-muted-foreground mb-6">
            Access the leads dashboard
          </p>

          <div className="space-y-4">
            <div>
              <Label htmlFor="admin-email" className="text-sm font-semibold">
                Email
              </Label>
              <Input
                id="admin-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@greenearthuk.co.uk"
                required
                autoComplete="username"
                data-testid="admin-login-email"
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label htmlFor="admin-password" className="text-sm font-semibold">
                Password
              </Label>
              <Input
                id="admin-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="current-password"
                data-testid="admin-login-password"
                className="mt-1.5 h-11"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="mt-6 w-full bg-primary hover:bg-primary/90 h-11 rounded-full"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing in...
              </>
            ) : (
              "Sign In"
            )}
          </Button>
        </form>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Authorized personnel only. All access is logged.
        </p>
      </div>
    </div>
  );
}
